/**
 * Field Validation Utilities for Templates V2
 *
 * Adapted from correspondence/utils/fieldValidation.ts to work with
 * ReadTemplateField from the v2 templates API.
 *
 * Key differences from v1:
 * - Uses `reference` (not `field_reference`)
 * - Uses `source: 'MANUAL' | 'CONTEXT'` (not `field_source: 'MANUAL' | 'MATTER_CONTEXT'`)
 * - Uses `context_key` directly (not `field_source_context_key`)
 * - Uses `default_value` directly (not `field_source_default_value`)
 * - Uses `required` boolean (not inferred from field_type)
 */
import type { ReadTemplateField } from '@/services/api/generated/templatesV2Service';

// ============================================================================
// Types
// ============================================================================

export type MatterContext = Record<string, string | null | undefined>;

/** Source of a field's resolved value */
export type FieldValueSource = 'override' | 'context' | 'default' | 'empty';

export interface FieldValueInfo {
  value: string;
  source: FieldValueSource;
}

export interface MissingFieldInfo {
  fieldId: string;
  fieldName: string;
  source: ReadTemplateField['source'];
  contextKey: string | null;
}

export interface FieldValidationResult {
  isValid: boolean;
  missingFields: MissingFieldInfo[];
  missingCount: number;
}

// ============================================================================
// Field Value Resolution
// ============================================================================

/**
 * Get the override key for a v2 template field.
 * Overrides are keyed by context_key or reference, NOT by field.id (UUID).
 */
export function getOverrideKey(field: ReadTemplateField): string {
  return field.context_key || field.reference;
}

/**
 * Resolve field value with source tracking.
 * Precedence: override -> context (via context_key) -> default -> empty
 */
export function getFieldValueInfo(
  field: ReadTemplateField,
  context: MatterContext,
  overrides: Record<string, string>,
): FieldValueInfo {
  const overrideKey = getOverrideKey(field);

  // 1. Check explicit overrides (keyed by context_key or reference)
  if (overrideKey in overrides) {
    const overrideValue = overrides[overrideKey];
    if (overrideValue !== undefined && overrideValue !== '') {
      return { value: overrideValue, source: 'override' };
    }
  }

  // 2. Check matter context (via context_key)
  if (field.context_key) {
    const ctxValue = context[field.context_key];
    if (ctxValue !== null && ctxValue !== undefined && ctxValue !== '') {
      return { value: String(ctxValue), source: 'context' };
    }
  }

  // 3. Check default value
  if (field.default_value) {
    return { value: field.default_value, source: 'default' };
  }

  return { value: '', source: 'empty' };
}

/**
 * Resolve field value (convenience wrapper returning just the value).
 */
export function resolveFieldValue(
  field: ReadTemplateField,
  context: MatterContext,
  overrides: Record<string, string>,
): string {
  return getFieldValueInfo(field, context, overrides).value;
}

// ============================================================================
// Display Helpers
// ============================================================================

/**
 * Get display name for a v2 template field.
 */
export function getFieldDisplayName(field: ReadTemplateField): string {
  return field.display_name || field.reference || field.context_key || 'Unknown';
}

// ============================================================================
// Validation
// ============================================================================

/**
 * Check if a field value is considered missing/empty.
 * For ARRAY fields stored as JSON strings, treats "[]" and empty arrays as missing.
 */
export function isFieldValueMissing(value: string): boolean {
  if (!value || value.trim() === '') return true;

  // Handle JSON array values (used by ARRAY fields)
  if (value.startsWith('[')) {
    try {
      const parsed: unknown = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.length === 0;
      }
    } catch {
      // Not valid JSON — fall through to string check
    }
  }

  return false;
}

/**
 * Check if a v2 field is required. Uses the explicit `required` flag.
 */
export function isFieldRequired(field: ReadTemplateField): boolean {
  return field.required;
}

/**
 * Validate template fields and return validation results.
 */
export function validateTemplateFields(
  fields: ReadTemplateField[],
  context: MatterContext,
  overrides: Record<string, string>,
): FieldValidationResult {
  const missingFields = fields
    .filter((field) => isFieldRequired(field))
    .reduce<MissingFieldInfo[]>((acc, field) => {
      const value = resolveFieldValue(field, context, overrides);
      if (isFieldValueMissing(value)) {
        acc.push({
          fieldId: field.id as string,
          fieldName: getFieldDisplayName(field),
          source: field.source,
          contextKey: field.context_key ?? null,
        });
      }
      return acc;
    }, []);

  return {
    isValid: missingFields.length === 0,
    missingFields,
    missingCount: missingFields.length,
  };
}

// ============================================================================
// Field Normalization
// ============================================================================

/**
 * Normalize API fieldsData into a flat ReadTemplateField array.
 * Handles: arrays, single objects, wrapped `{ fields: [...] }` responses.
 * Filters out SIGNING fields.
 */
export function normalizeTemplateFields(fieldsData: unknown): ReadTemplateField[] {
  let fields: ReadTemplateField[];
  if (Array.isArray(fieldsData)) {
    fields = fieldsData as ReadTemplateField[];
  } else if (!fieldsData || typeof fieldsData !== 'object') {
    return [];
  } else {
    const wrappedRows = (fieldsData as { fields?: unknown }).fields;
    if (Array.isArray(wrappedRows)) {
      fields = wrappedRows as ReadTemplateField[];
    } else if ('id' in (fieldsData as Record<string, unknown>)) {
      fields = [fieldsData as ReadTemplateField];
    } else {
      return [];
    }
  }
  return fields.filter((f) => f.source !== 'SIGNING');
}
